import { GridColumn } from '@progress/kendo-react-grid'
import React from 'react'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'

import {
  compile,
  createBreadcrumbWithI18n,
} from '~/apps/ninesegsAnalyzer/routes/paths'
import { HStack } from '~/components/elements/HStack'
import {
  PageContainer,
  type PageContainerProps,
} from '~/components/elements/PageContainer'
import { StyledButton } from '~/components/elements/StyledButton'
import {
  StyledGrid,
  type StyledGridProps,
} from '~/components/elements/StyledGrid'
import { VStack } from '~/components/elements/VStack'
import { BetterLink } from '~/components/features/BetterLink'
import { useLanguageTextWidth } from '~/hooks/useLanguageTextWidth'
import { useScrollToBottom } from '~/hooks/useScrollToBottom'

import { useBrandAccountId } from '../hooks/useBrandAccountId'
import { useDocumentReportsInfiniteQuery } from '../hooks/useDocumentReportsInfiniteQuery'

import { CommandsCell } from './CommandsCell'
import { CreateButton } from './CreateButton'
import { TitleCell } from './TitleCell'

export const DocumentReportList = () => {
  const brandAccountId = useBrandAccountId()
  const { t } = useTranslation()

  const createBreadcrumb = React.useMemo(() => createBreadcrumbWithI18n(t), [t])
  const breadcrumbs = React.useMemo<PageContainerProps['breadcrumbs']>(
    () => [
      createBreadcrumb('top', {}),
      createBreadcrumb('documentManagement'),
      createBreadcrumb('documentReportList', { brandAccountId }),
    ],
    [brandAccountId, createBreadcrumb],
  )

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useDocumentReportsInfiniteQuery()
  const documentReports = React.useMemo(
    () => R.pipe(data?.pages ?? [], R.flat()),
    [data?.pages],
  )

  const handleGridScroll = useScrollToBottom<EH<StyledGridProps, 'onScroll'>>({
    getElement: (event) => event.nativeEvent.target,
    onScrollToBottom: () => {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage()
      }
    },
  })

  const binIcon = t('bin') === '' ? 'delete' : undefined
  const shareButtonWidth = useLanguageTextWidth({ 'ja-JP': 82, 'en-US': 92 })
  const duplicateButtonWidth = useLanguageTextWidth({
    'ja-JP': 82,
    'en-US': 116,
  })
  const deleteButtonWidth = useLanguageTextWidth({ 'ja-JP': 82, 'en-US': 144 })
  const commandsCellWidth =
    12 +
    shareButtonWidth +
    4 +
    duplicateButtonWidth +
    4 +
    deleteButtonWidth +
    12

  return (
    <PageContainer breadcrumbs={breadcrumbs}>
      <VStack spacing={2} verticalAppearance="stretch">
        <HStack alignSelf="end" spacing={2}>
          <BetterLink
            logEventName="Open document trash"
            to={compile('documentReportArchiveList', { brandAccountId })}
          >
            <StyledButton fillMode="outline" icon={binIcon}>
              {t('bin')}
            </StyledButton>
          </BetterLink>

          <CreateButton />
        </HStack>

        <StyledGrid data={documentReports} onScroll={handleGridScroll}>
          <GridColumn cell={TitleCell} title={t('name')} />

          <GridColumn field="writer" title={t('owner')} />

          <GridColumn field="updatedAtText" title={t('last_modified')} />

          <GridColumn cell={CommandsCell} width={commandsCellWidth} />
        </StyledGrid>
      </VStack>
    </PageContainer>
  )
}
